import * as THREE from 'three';

const POWM_MAGIC = 'POWM';
const POWM_HEADER_BYTES = 24;
const POWM_BONE_BYTES = 100;
const SOURCE_UNITS_TO_METRES = 0.0254;

function sourceMatrixToThree(values) {
  const source = new THREE.Matrix4().fromArray(values);
  const toThree = new THREE.Matrix4().makeScale(
    SOURCE_UNITS_TO_METRES,
    -SOURCE_UNITS_TO_METRES,
    SOURCE_UNITS_TO_METRES,
  );
  const toSource = new THREE.Matrix4().makeScale(
    1 / SOURCE_UNITS_TO_METRES,
    -1 / SOURCE_UNITS_TO_METRES,
    1 / SOURCE_UNITS_TO_METRES,
  );
  return toThree.multiply(source).multiply(toSource);
}

function readAscii(view, offset, length) {
  let result = '';
  for (let index = 0; index < length; index += 1) {
    const value = view.getUint8(offset + index);
    if (value === 0) break;
    result += String.fromCharCode(value);
  }
  return result;
}

export function parsePowOperatorGeometry(buffer, assetName = 'operator.powmesh') {
  const view = new DataView(buffer);
  if (view.byteLength < POWM_HEADER_BYTES) throw new Error('CS2RADAR-OPERATOR-001: truncated POWM header');

  const magic = readAscii(view, 0, 4);
  const version = view.getUint32(4, true);
  const boneCount = view.getUint32(8, true);
  const vertexCount = view.getUint32(12, true);
  const indexCount = view.getUint32(16, true);
  const flags = view.getUint32(20, true);
  const legacy = version === 2 && flags === 1;
  const colored = version === 3 && flags === 3;
  if (magic !== POWM_MAGIC || (!legacy && !colored)) {
    throw new Error(`CS2RADAR-OPERATOR-002: unsupported POWM ${magic}/${version}/${flags}`);
  }
  if (!boneCount || boneCount > 256 || !vertexCount || !indexCount || indexCount % 3 !== 0) {
    throw new Error('CS2RADAR-OPERATOR-003: invalid POWM counts');
  }

  const vertexBytes = colored ? 52 : 48;
  const vertexOffset = POWM_HEADER_BYTES + boneCount * POWM_BONE_BYTES;
  const indexOffset = vertexOffset + vertexCount * vertexBytes;
  const expectedBytes = indexOffset + indexCount * 4;
  if (expectedBytes !== view.byteLength) throw new Error('CS2RADAR-OPERATOR-004: POWM byte count mismatch');

  const bones = [];
  for (let index = 0; index < boneCount; index += 1) {
    const source = POWM_HEADER_BYTES + index * POWM_BONE_BYTES;
    const parent = view.getInt32(source + 32, true);
    if (parent >= index || parent < -1) throw new Error('CS2RADAR-OPERATOR-007: invalid bone hierarchy');
    const inverseBindValues = new Float32Array(16);
    for (let component = 0; component < 16; component += 1) {
      inverseBindValues[component] = view.getFloat32(source + 36 + component * 4, true);
    }
    bones.push({
      name: readAscii(view, source, 32),
      parent,
      inverseBind: sourceMatrixToThree(inverseBindValues).toArray(),
    });
  }

  const positions = new Float32Array(vertexCount * 3);
  const normals = new Float32Array(vertexCount * 3);
  const skinIndices = new Uint16Array(vertexCount * 4);
  const skinWeights = new Float32Array(vertexCount * 4);
  const colors = colored ? new Uint8Array(vertexCount * 4) : null;
  for (let index = 0; index < vertexCount; index += 1) {
    const source = vertexOffset + index * vertexBytes;
    const target = index * 3;
    // POWM is Source-space. Radar Three.js uses Y-up, with local forward at -Z.
    positions[target] = view.getFloat32(source, true) * SOURCE_UNITS_TO_METRES;
    positions[target + 1] = -view.getFloat32(source + 4, true) * SOURCE_UNITS_TO_METRES;
    positions[target + 2] = view.getFloat32(source + 8, true) * SOURCE_UNITS_TO_METRES;
    normals[target] = view.getFloat32(source + 12, true);
    normals[target + 1] = -view.getFloat32(source + 16, true);
    normals[target + 2] = view.getFloat32(source + 20, true);
    const skinTarget = index * 4;
    let weightTotal = 0;
    for (let influence = 0; influence < 4; influence += 1) {
      const joint = view.getUint16(source + 24 + influence * 2, true);
      const weight = view.getFloat32(source + 32 + influence * 4, true);
      if (joint >= boneCount || !Number.isFinite(weight) || weight < 0) {
        throw new Error('CS2RADAR-OPERATOR-008: invalid skin influence');
      }
      skinIndices[skinTarget + influence] = joint;
      skinWeights[skinTarget + influence] = weight;
      weightTotal += weight;
    }
    if (weightTotal < 0.98 || weightTotal > 1.02) {
      throw new Error('CS2RADAR-OPERATOR-009: invalid skin weight sum');
    }
    if (colors) {
      const colorTarget = index * 4;
      colors[colorTarget] = view.getUint8(source + 48);
      colors[colorTarget + 1] = view.getUint8(source + 49);
      colors[colorTarget + 2] = view.getUint8(source + 50);
      colors[colorTarget + 3] = view.getUint8(source + 51);
    }
  }

  const indices = new Uint32Array(indexCount);
  for (let index = 0; index < indexCount; index += 1) {
    indices[index] = view.getUint32(indexOffset + index * 4, true);
    if (indices[index] >= vertexCount) throw new Error('CS2RADAR-OPERATOR-005: POWM index out of range');
  }

  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
  geometry.setAttribute('normal', new THREE.BufferAttribute(normals, 3));
  geometry.setAttribute('skinIndex', new THREE.Uint16BufferAttribute(skinIndices, 4));
  geometry.setAttribute('skinWeight', new THREE.Float32BufferAttribute(skinWeights, 4));
  if (colors) geometry.setAttribute('color', new THREE.BufferAttribute(colors, 4, true));
  geometry.setIndex(new THREE.BufferAttribute(indices, 1));
  geometry.computeBoundingBox();
  geometry.computeBoundingSphere();
  geometry.userData = {
    asset: assetName,
    source: 'CS2 official third-person mesh with baked base-color material',
    format: colored ? 'POWM-v3-RGBA8' : 'POWM-v2',
    vertexColors: Boolean(colors),
    skinned: true,
    bones,
    vertices: vertexCount,
    triangles: indexCount / 3,
  };
  return geometry;
}

export function createPowOperatorSkinnedMesh(geometry, material) {
  const definitions = geometry?.userData?.bones;
  if (!Array.isArray(definitions) || !definitions.length) return new THREE.Mesh(geometry, material);

  const bones = definitions.map((definition) => {
    const bone = new THREE.Bone();
    bone.name = definition.name;
    return bone;
  });
  const inverseBinds = definitions.map((definition) => new THREE.Matrix4().fromArray(definition.inverseBind));
  const worldBinds = inverseBinds.map((matrix) => matrix.clone().invert());

  definitions.forEach((definition, index) => {
    const local = definition.parent >= 0
      ? worldBinds[definition.parent].clone().invert().multiply(worldBinds[index])
      : worldBinds[index].clone();
    local.decompose(bones[index].position, bones[index].quaternion, bones[index].scale);
    if (definition.parent >= 0) bones[definition.parent].add(bones[index]);
  });

  const mesh = new THREE.SkinnedMesh(geometry, material);
  definitions.forEach((definition, index) => {
    if (definition.parent < 0) mesh.add(bones[index]);
  });
  mesh.updateMatrixWorld(true);
  const skeleton = new THREE.Skeleton(bones, inverseBinds);
  mesh.bind(skeleton, new THREE.Matrix4());
  mesh.normalizeSkinWeights();
  mesh.frustumCulled = false;
  mesh.userData.poseRig = {
    bones,
    byName: new Map(bones.map((bone) => [bone.name, bone])),
    indexByName: new Map(bones.map((bone, index) => [bone.name, index])),
    bindPose: bones.map((bone) => ({
      position: bone.position.clone(),
      quaternion: bone.quaternion.clone(),
      scale: bone.scale.clone(),
    })),
  };
  return mesh;
}

export async function loadPowOperatorGeometry(url) {
  const response = await fetch(url, { cache: 'force-cache' });
  if (!response.ok) throw new Error(`CS2RADAR-OPERATOR-006: ${response.status} ${response.statusText}`);
  return parsePowOperatorGeometry(await response.arrayBuffer(), String(url).split('/').at(-1));
}
